import React, { useEffect, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import { Project, ProjectWithId } from '../types/index';
import { db } from '../utils/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/ui/LoadingScreen';
import '../styles/Dashboard.css';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState<ProjectWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isStaff = user?.role === 'employee' || user?.role === 'admin';

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;

      try {
        let q;
        if (isStaff) {
          // Employees see all projects
          q = query(collection(db, 'projects'), orderBy('startDate', 'desc'));
        } else {
          // Clients see only their projects - filter client-side to avoid Firebase index issues
          q = query(collection(db, 'projects'), orderBy('startDate', 'desc'));
        }

        const querySnapshot = await getDocs(q);
        const projectsData: ProjectWithId[] = [];

        querySnapshot.forEach((doc) => {
          const projectData = doc.data() as Project;
          const project = { id: doc.id, ...projectData };

          // For clients, filter to only show projects they're assigned to
          // NOTE: We use emails instead of UIDs because emails are more stable and meaningful
          if (isStaff || project.clientIds?.includes(user.email)) {
            projectsData.push(project);
          }
        });

        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user]);

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return <LoadingScreen size="large" />;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>LCTF Project Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {user?.name}</span>
            <span className="user-role">({user?.role})</span>
          </div>
        </div>
        <div className="header-actions">
          {isStaff && (
            <>
              <button
                onClick={() => navigate('/crm')}
                className="crm-button"
              >
                CRM Dashboard
              </button>
              <button
                onClick={() => navigate('/clients')}
                className="manage-clients-button"
              >
                Manage Clients
              </button>
            </>
          )}
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="projects-section">
          <div className="section-header">
            <h2>
              {isStaff ? 'All Projects' : 'Your Projects'} ({projects.length})
            </h2>
          </div>

          {projects.length === 0 ? (
            <div className="empty-state">
              <p>
                {isStaff
                  ? 'No projects found. Create your first project to get started.'
                  : 'No projects assigned to you yet. Contact LCTF for project updates.'
                }
              </p>
            </div>
          ) : (
            <div className="projects-grid">
              {projects.map((project) => (
                <div key={project.id} className="project-card">
                  <div className="project-header">
                    <h3>{project.name}</h3>
                    <span className={`status-badge status-${project.status.replace(' ', '-')}`}>
                      {project.status.replace('-', ' ')}
                    </span>
                  </div>
                  <p className="project-location">{project.location}</p>
                  <p className="project-description">{project.description}</p>
                  <div className="project-details">
                    <div className="detail-item">
                      <span className="label">Timber Type:</span>
                      <span>{project.timberType}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Square Footage:</span>
                      <span>{project.squareFootage.toLocaleString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Budget:</span>
                      <span>${project.budget.toLocaleString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Progress:</span>
                      <span>{project.progress}%</span>
                    </div>
                    {isStaff && project.clientIds && (
                      <div className="detail-item">
                        <span className="label">Clients:</span>
                        <span>{project.clientIds.length} assigned</span>
                      </div>
                    )}
                  </div>
                  <div className="project-actions">
                    <Link to={`/project/${project.id}`} className="view-project-button">
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
